import ArtifactCard from './ArtifactCard'
import type { PodcastEpisode, YouTubeVideo, Artifact } from '@/types'

interface LatestGridProps {
  podcast?: PodcastEpisode
  video?: YouTubeVideo
  artifact?: Artifact
}

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const hrs = Math.floor(mins / 60)
  if (hrs > 0) {
    return `${hrs}h ${mins % 60}m`
  }
  return `${mins}m`
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function ComingSoonCard({ title, type }: { title: string; type: 'video' | 'artifact' }) {
  const icons = {
    video: (
      <svg className="w-12 h-12 text-archive-gold/50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
      </svg>
    ),
    artifact: (
      <svg className="w-12 h-12 text-archive-gold/50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
      </svg>
    ),
  }

  return (
    <div className="relative rounded-2xl border border-archive-ash/30 bg-archive-charcoal/50 p-8 text-center">
      <div className="flex flex-col items-center justify-center min-h-[200px]">
        {icons[type]}
        <h3 className="mt-4 font-display text-lg text-archive-cream">{title}</h3>
        <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-archive-gold/30 bg-archive-gold/10 px-4 py-2">
          <div className="h-1.5 w-1.5 animate-pulse rounded-full bg-archive-gold" />
          <span className="text-xs uppercase tracking-wider text-archive-gold">Coming Soon</span>
        </div>
      </div>
    </div>
  )
}

export default function LatestGrid({ podcast }: LatestGridProps) {
  const items = []

  if (podcast) {
    items.push(
      <ArtifactCard
        key="podcast"
        title={podcast.title}
        description={podcast.summary}
        imageUrl={
          podcast.cover_image_url ||
          podcast.series_cover_image_url ||
          `/images/podcasts/${podcast.archive_key}.jpg`
        }
        linkTo={`/podcast/${podcast.id}`}
        type="podcast"
        archiveKey={podcast.archive_key}
        meta={`${formatDate(podcast.published_at)} • ${formatDuration(podcast.duration_sec)}`}
      />
    )
  }

  // Conversations - live (curated showcase from /conversations)
  items.push(
    <ArtifactCard
      key="conversation"
      title="Ancient Egyptian Civilization — Part 1"
      description="A conversation between the Curators, exploring the river that birthed a civilization."
      imageUrl="https://img.youtube.com/vi/m_-yVAfl5JE/hqdefault.jpg"
      linkTo="/conversations"
      type="video"
      typeLabel="conversation"
      archiveKey="civilization"
    />
  )

  // Artifacts - Coming Soon
  items.push(
    <ComingSoonCard key="artifact" title="Archive Artifacts" type="artifact" />
  )

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {items}
    </div>
  )
}
